import React from 'react';

import { Skeleton, Typography } from '@mui/material';
import { AlineButton } from 'src/components';
import { useAccessGuard } from 'src/components/auth';
import { Apollo } from 'src/gql/generated';
import { redirectTo } from 'src/helpers';
import { useGetOrgMembersBySeatType } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { Checkout } from './Checkout';

export const useBillingStyles = makeStyles()(theme => ({
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 'auto',
  },
  container: {
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 4,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(2),
    width: 360,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  seats: {
    alignItems: 'baseline',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(0.5),
  },
  seatsRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
}));

export function SubscriptionPlan(): JSX.Element {
  const { classes } = useBillingStyles();
  const [createBillingSessionUrl, { loading: creatingSessionUrl }] =
    Apollo.useCreateBillingSessionUrlMutation();
  const {
    basicSeatOrgMembers,
    currentUser,
    fullSeatOrgMembers,
    loading,
    numBasicSeatsPurchased,
    numFullSeatsPurchased,
  } = useGetOrgMembersBySeatType({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const tierString = getTierDisplayString(currentUser?.org.alineSubscription?.tier ?? null);
  const isOnFreePlan = !currentUser?.org.alineSubscription;
  const numFullSeatsUsed = fullSeatOrgMembers.length;
  const numBasicSeatsUsed = basicSeatOrgMembers.length;
  const hasSubscription = Boolean(currentUser?.org.alineSubscription);
  const handleViewInvoices = useAccessGuard({
    callback: async () => {
      const { data } = await createBillingSessionUrl();
      if (data) redirectTo(data.createBillingSessionUrl);
    },
    minRole: Apollo.OrgRole.Admin,
  });

  return (
    <div className={classes.container}>
      <div className={classes.content}>
        <Typography variant="caption">Current Plan</Typography>
        <AlineTier loading={loading} tierString={tierString} />
        <div className={classes.seatsRow}>
          <div className={classes.seats}>
            <Typography variant="subtitle2">Full Users:</Typography>
            <Typography variant="body1">
              {isOnFreePlan
                ? 'N/A'
                : `Using ${numFullSeatsUsed} of ${numFullSeatsPurchased} seat(s).`}
            </Typography>
          </div>
          <div className={classes.seats}>
            <Typography variant="subtitle2">Basic Users:</Typography>
            <Typography variant="body1">
              {isOnFreePlan
                ? 'N/A'
                : `Using ${numBasicSeatsUsed} of ${numBasicSeatsPurchased} seat(s).`}
            </Typography>
          </div>
        </div>
      </div>
      <div className={classes.buttonRow}>
        <Checkout />
        {hasSubscription && (
          <AlineButton
            color="primary"
            loading={creatingSessionUrl}
            variant="outlined"
            onClick={handleViewInvoices}
          >
            View Invoices
          </AlineButton>
        )}
      </div>
    </div>
  );
}

interface AlineTierProps {
  loading: boolean;
  tierString: string;
}

function AlineTier({ loading, tierString }: AlineTierProps): JSX.Element {
  if (loading)
    return (
      <Typography variant="h2">
        <Skeleton variant="text" width={120} />
      </Typography>
    );

  return <Typography variant="h2">{tierString}</Typography>;
}

export function getTierDisplayString(tier: Apollo.SubscriptionTier | null): string {
  if (!tier) return 'Free';

  switch (tier) {
    case Apollo.SubscriptionTier.Team:
      return 'Team';
    case Apollo.SubscriptionTier.Enterprise:
      return 'Enterprise';
  }
}
